import { Portal } from '@reach/portal'
import { AnimatePresence, motion } from 'framer-motion'
import { rgba } from 'polished'
import React, { useRef } from 'react'
import useClickAway from 'react-use/lib/useClickAway'

import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import useBodyScrollLock from 'hooks/use-body-scroll-lock'
import theme from 'theme'

export const offcanvasVariables: (
  theme: Record<string, any>
) => Record<string, any> = (theme) => ({
  padding: theme.space[5],
  minWidth: theme.bp[1]
})

type OffCanvasTitleProps = {
  textStyle?: Record<string, any>
  minWidth?: number
  darkMode?: boolean
  setIsOpen?: (open: boolean) => void
  subtitle?: React.ReactNode
  title?: React.ReactNode
}

export const OffCanvasTitle: React.FC<OffCanvasTitleProps> = ({
  setIsOpen,
  title,
  subtitle,
  darkMode,
  minWidth,
  textStyle = {
    color: 'normal',
    weight: 'bold'
  },
  ...rest
}) => {
  return (
    <header
      css={[
        {
          position: 'relative',
          display: 'flex',
          paddingTop: offcanvasVariables(theme).padding,
          paddingRight: offcanvasVariables(theme).padding,
          paddingBottom: offcanvasVariables(theme).padding,
          paddingLeft: offcanvasVariables(theme).padding,
          backgroundColor: darkMode
            ? theme.colors.steel[2]
            : theme.colors.white[0],
          minWidth: minWidth || offcanvasVariables(theme).minWidth
        },
        textStyle
      ]}
      {...rest}
    >
      <div
        css={{
          marginRight: theme.space[2]
        }}
      >
        {title && (
          <Text
            data-test-offcanvas-title
            as="h2"
            color={darkMode ? 'white' : null}
          >
            {title}
          </Text>
        )}
        {subtitle && (
          <Text
            data-test-offcanvas-subtitle
            as="h4"
            color={darkMode ? 'white' : 'normal'}
            css={{ marginTop: theme.space[2] }}
            weight="bold"
          >
            {subtitle}
          </Text>
        )}
      </div>
      <TextButton
        dark
        css={{
          marginLeft: 'auto',
          color: darkMode ? theme.colors.white[0] : theme.colors.steel[2],

          '&:hover, &:focus, &:active': {
            color: darkMode ? theme.colors.white[0] : theme.colors.steel[2]
          }
        }}
        onClick={() => setIsOpen(false)}
      >
        <Icon
          aria-label="close"
          css={{
            fontSize: theme.fontSizes[4]
          }}
          icon="close"
        />
      </TextButton>
    </header>
  )
}

type OffCanvasProps = {
  contentStyles?: Record<string, any>
  footer?: React.ReactNode
  minWidth?: number
  onComplete?: (isOpen: boolean) => void
  open: boolean
  position?: 'left' | 'right'
  renderHeader?: (values: Record<string, any>) => React.ReactNode
  setIsOpen: (open: boolean) => void
  subtitle?: React.ReactNode
  title?: React.ReactNode
}

const OffCanvas: React.FC<OffCanvasProps> = ({
  position = 'right',
  onComplete = () => undefined,
  contentStyles,
  footer,
  minWidth,
  open,
  renderHeader,
  setIsOpen,
  subtitle,
  title,
  children,
  ...props
}) => {
  const offCanvasBackgroundRef = useRef()
  const offCanvasPanelRef = useRef()
  const { lock, unlock } = useBodyScrollLock(offCanvasBackgroundRef)
  useClickAway(offCanvasPanelRef, () => {
    unlock()
    setIsOpen(false)
  })

  const canvasVariants = {
    shown: {
      x: 0,
      transition: {
        onComplete: () => {
          onComplete(true)
          lock()
        },
        type: 'spring',
        stiffness: 550,
        damping: 50
      }
    },
    hidden: {
      x: position === 'right' ? '100%' : '-100%',
      transition: {
        type: 'spring',
        stiffness: 550,
        damping: 50,
        onComplete: () => {
          onComplete(false)
          unlock()
        }
      }
    }
  }

  return (
    <Portal>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={offCanvasBackgroundRef}
            data-test-off-canvas
            className="OffCanvas"
            css={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: theme.zIndices[5],
              overflowX: 'hidden'
            }}
            transition={{
              when: 'beforeChildren'
            }}
            {...props}
          >
            <motion.div
              animate={{
                opacity: open ? 1 : 0
              }}
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflowX: 'hidden',
                backgroundColor: rgba(theme.colors.steel[2], theme.opacity[0])
              }}
              exit={{
                opacity: 0
              }}
              initial={{
                opacity: 0
              }}
              transition={{
                type: 'spring',
                stiffness: 550,
                damping: 50
              }}
            />
            <motion.div
              ref={offCanvasPanelRef}
              animate={open ? 'shown' : 'hidden'}
              className="OffCanvas__content"
              css={[
                {
                  background: theme.colors.white[0],
                  position: 'absolute',
                  top: 0,
                  right: position === 'right' && 0,
                  left: position === 'left' && 0,
                  height: '100%',
                  width: '100%',
                  zIndex: theme.zIndices[1],
                  overflow: 'auto',
                  boxShadow:
                    position === 'right'
                      ? `-7.5px 0 17.5px ${rgba(theme.colors.steel[2], 0.15)}`
                      : `7.5px 0 17.5px ${rgba(theme.colors.steel[2], 0.15)}`,

                  [theme.mq[1]]: {
                    width: minWidth || offcanvasVariables(theme).minWidth
                  }
                },
                contentStyles
              ]}
              exit="hidden"
              initial="hidden"
              variants={canvasVariants}
            >
              {typeof renderHeader === 'function' ? (
                renderHeader({ title, subtitle, setIsOpen, minWidth })
              ) : title ? (
                <OffCanvasTitle
                  minWidth={minWidth}
                  setIsOpen={setIsOpen}
                  subtitle={subtitle}
                  title={title}
                />
              ) : (
                <div />
              )}

              <div
                className="OffCanvas__body"
                css={{
                  position: 'relative',
                  minWidth: minWidth || offcanvasVariables(theme).minWidth,
                  padding: offcanvasVariables(theme).padding
                }}
              >
                {children}
              </div>
              {footer && (
                <div
                  css={{
                    minWidth: minWidth || offcanvasVariables(theme).minWidth,
                    padding: offcanvasVariables(theme).padding
                  }}
                >
                  {footer}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  )
}

export default OffCanvas
